import dayjs from "dayjs"

/**
 * Maps a single server add-on record to the form's camelCase shape.
 * Exported so AddOns.jsx can reuse it after an immediate save API response.
 */
export const mapServerAddOn = (a) => ({
  id: a.id,
  title: a.title,
  category: a.add_on_category?.id ?? null,
  categoryName: a.add_on_category?.name ?? null,
  price: a.price,
  description: a.description,
  minPerOrder: a.min_per_order ?? null,
  maxPerOrder: a.max_per_order ?? null,
  enabled: a.enabled !== false,
  photo: a.photo_url ?? null,
})

/**
 * Expands trip.available_date_ranges into the flat form fields
 * SchedulingStep expects: timeBlocks (index array) + seasonStart_N / seasonEnd_N.
 */
const buildSeasonalRanges = (trip) => {
  const ranges = trip.available_date_ranges
  if (!trip.seasonal || !ranges?.length) {
    return { timeBlocks: trip.seasonal ? [0] : [] }
  }
  const result = { timeBlocks: ranges.map((_, i) => i) }
  ranges.forEach((range, i) => {
    if (range.start) result[`seasonStart_${i}`] = dayjs(range.start)
    if (range.end) result[`seasonEnd_${i}`] = dayjs(range.end)
  })
  return result
}

/**
 * Converts trip server data → AntD form field values.
 * Called once by WizardProvider after the trip is loaded.
 *
 * Rules:
 * - No backend key changes. Field names here match what Form.Item name="" expects.
 * - available_hours strings are converted to dayjs (DaysAndHours renders TimePicker).
 * - Seasonal date ranges (seasonStart_N / seasonEnd_N) are built by buildSeasonalRanges
 *   and spread in. SchedulingStep will skip re-initialization if these are already set.
 */
export const buildDefaultValues = (trip, { availableCategories = [] } = {}) => {
  if (!trip?.id) return {}

  let categories = []
  if (Array.isArray(trip.categories)) {
    categories = trip.categories
  } else if (trip.categories) {
    categories = [trip.categories]
  }

  const availableHours = (trip.available_hours || []).map(({ start, end }) => ({
    start: start ? dayjs(start, "HH:mm") : null,
    end: end ? dayjs(end, "HH:mm") : null
  }))

  // API returns marketplace_tag_ids (array of IDs); fall back to marketplace_tags for legacy shape.
  const marketplaceTags = trip.marketplace_tag_ids?.length
    ? trip.marketplace_tag_ids
    : trip.marketplace_tags || undefined

  return {
    // ── Details ─────────────────────────────────────────────────────────────
    title: trip.title,
    description: trip.description,
    // Single-select — store the category ID so the Select can match the option value
    categories: (() => {
      if (!categories.length) return undefined
      const raw = categories[0]
      if (typeof raw === "object" && raw.id) return raw.id
      // raw is a name string — resolve to ID from availableCategories
      const match = availableCategories.find((a) => a.name.toLowerCase() === String(raw).toLowerCase())
      return match?.id
    })(),
    marketplace_tags: marketplaceTags?.length ? marketplaceTags : undefined,
    min_guests: trip.min_guests,
    max_guests: trip.max_guests,
    languages: trip.languages || [],
    skill_levels: trip.skill_levels || [],
    included_items: trip.included_items,
    bring_items: trip.bring_items,
    accessibility_enabled: trip.accessibility_enabled,
    alcohol_allowed: trip.alcohol_allowed,
    children_allowed: trip.children_allowed,
    publish_on_guides_site: trip.publish_on_guides_site,
    publish_on_platform: trip.publish_on_platform,

    // ── Scheduling ───────────────────────────────────────────────────────────
    time_zone: trip.time_zone,
    pricing_type: trip.pricing_type || "per_person",
    payment_type: trip.payment_type || "full",
    deposit_percentage: trip.deposit_percentage || 10,
    full_payment_required: trip.full_payment_required,
    licenses_required: trip.licenses_required,
    licenses_list: trip.licenses_list,
    seasonal: trip.seasonal,
    isSeasonalExperience: trip.seasonal === true,
    recurringYearly: trip.available_date_ranges?.length > 0 ? trip.recurring_yearly : true,
    available_days: trip.available_days,
    available_hours: availableHours.length ? availableHours : undefined,
    ...buildSeasonalRanges(trip),

    // ── Tickets ──────────────────────────────────────────────────────────────
    ticket_types: trip.ticket_types?.length ? trip.ticket_types : undefined,
    // Seed base prices from server so the Pricing step shows saved values on edit.
    ticketTypePrices: Object.fromEntries(
      (trip.ticket_types || []).map((t) => [t.id, { enabled: t.enabled !== false, price: t.price > 0 ? t.price : null }])
    ),
    ticket_duration: trip.ticket_duration || "single_day",
    min_tickets: trip.min_tickets,
    max_tickets: trip.max_tickets,
    ticket_description: trip.ticket_description,

    // ── Ticketed Scheduling ───────────────────────────────────────────────────
    duration_value: trip.duration_value,
    duration_unit: trip.duration_unit,
    cut_off_value: trip.cut_off_value,
    cut_off_unit: trip.cut_off_unit || "minute",
    low_availability_enabled: trip.low_availability_enabled ?? true,
    low_availability_threshold: trip.low_availability_threshold ?? 1,

    // ── Location ─────────────────────────────────────────────────────────────
    address: trip.address,
    additional_directions: trip.additional_directions,

    // ── Ticketed Schedule ─────────────────────────────────────────────────────
    // Server slots have { id, starts_at } but no label — derive it so TicketedSchedule can display them.
    // Uses the same format as formatSlotLabel in ScheduleExperienceModal/utils.js.
    time_slots: (trip.time_slots || []).map((slot) => ({
      ...slot,
      label: slot.label || dayjs(slot.starts_at).format("ddd MMM D, YYYY h:mm A")
    })),

    // ── Availability Schedule ─────────────────────────────────────────────────
    availability_schedules: (trip.availability_schedules || []).map((s) => ({
      id: s.id,
      name: s.name ?? "",
      days: s.days ?? [],
      from_time: s.from_time ?? null,
      to_time: s.to_time ?? null,
      interval: s.interval ?? null,
      start_times: s.start_times ?? [],
    })),
    schedule_overrides: (trip.schedule_overrides || []).map((o) => ({
      id: o.id,
      name: o.name ?? "",
      date_ranges: o.date_ranges ?? [],
      from_time: o.from_time ?? null,
      to_time: o.to_time ?? null,
      interval: o.interval ?? null,
      start_times: o.start_times ?? [],
    })),

    // ── Additional Info ───────────────────────────────────────────────────────
    add_ons: (trip.add_ons || []).map(mapServerAddOn),
    // digital_waiver_experiences is the join table; extract the waiver IDs for the multi-select
    digital_waiver: (trip.digital_waiver_experiences || []).map((dw) => dw.digital_waiver_id),
    question_template: (trip.questionnaire_templates || []).map((qt) => qt.id)
  }
}
